import { Text } from '@noorden-insurtech/alba-design-system';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  X,
} from 'lucide-react';
import { useState } from 'react';
import type { GroupLifeAsyncImportUploadError } from '@/services';
import type { AsyncImportProgress } from '@/features/quotes/hooks/group-life/use-async-import.hook';

type AsyncImportStatus = 'PARSING' | 'HYDRATING' | 'COMPLETED' | 'FAILED';

interface Props {
  status: AsyncImportStatus;
  progress: AsyncImportProgress;
  errors?: GroupLifeAsyncImportUploadError[];
  /** Quando definido, exibe botão de fechar. O parent decide o que fazer (em
   *  geral: limpar o state `asyncImport` + remover do localStorage). */
  onDismiss?: () => void;
}

/**
 * Widget FLUTUANTE e NÃO-BLOQUEANTE no canto inferior direito da tela,
 * mostrando o progresso do upload assíncrono. O corretor segue navegando e
 * cadastrando subestipulantes/vidas enquanto o batch processa em background.
 *
 * Estados:
 *  - PARSING: spinner + barra de progresso + contagem X/Y.
 *  - COMPLETED: ícone de sucesso (some sozinho ~3s depois — controlado pelo
 *    parent, este componente só renderiza enquanto `status` é informado).
 *  - FAILED: ícone de alerta + lista colapsável de erros.
 *
 * Pode ser minimizado pelo usuário (chevron) — só o status icon fica visível
 * num pílula compacta.
 */
export function AsyncImportOverlay({
  status,
  progress,
  errors = [],
  onDismiss,
}: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedErrors, setExpandedErrors] = useState(false);

  const isFailed = status === 'FAILED';
  const isCompleted = status === 'COMPLETED';
  const surface =
    'bg-alba-white-500 border border-alba-gray-200 shadow-lg border-l-4';
  const accent = isFailed
    ? 'border-l-alba-error-red-500'
    : isCompleted
      ? 'border-l-alba-success-green-500'
      : 'border-l-alba-blue-500';

  const isHydrating = status === 'HYDRATING';
  const headline = isFailed
    ? 'Importação interrompida'
    : isCompleted
      ? 'Importação concluída'
      : isHydrating
        ? 'Finalizando…'
        : progress.percent >= 80
          ? 'Quase pronto…'
          : progress.percent > 0
            ? 'Processando vidas…'
            : 'Enviando ao sistema…';

  const subhead = isFailed
    ? `${errors.length} erro(s) reportado(s). Veja os detalhes abaixo.`
    : isCompleted
      ? `${progress.total} vida(s) importada(s) com sucesso.`
      : isHydrating
        ? 'Sincronizando vidas — só mais alguns segundos.'
        : 'Você pode continuar trabalhando — avisamos quando terminar.';

  const Icon = () => {
    if (isFailed) {
      return (
        <AlertTriangle
          className='h-5 w-5 text-alba-error-red-500'
          strokeWidth={1.8}
        />
      );
    }
    if (isCompleted) {
      return (
        <CheckCircle2
          className='h-5 w-5 text-alba-success-green-500'
          strokeWidth={1.8}
        />
      );
    }
    return (
      <Loader2
        className='h-5 w-5 animate-spin text-alba-blue-500'
        strokeWidth={1.8}
      />
    );
  };

  // Modo minimizado: pílula compacta no canto.
  if (collapsed) {
    return (
      <button
        type='button'
        onClick={() => setCollapsed(false)}
        className={`fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full ${surface} ${accent} px-3 py-2 transition hover:shadow-xl`}
        aria-label='Expandir status da importação'
      >
        <Icon />
        <Text className='text-xs font-semibold text-alba-black-500'>
          {isFailed
            ? 'Erro na importação'
            : isCompleted
              ? 'Importação concluída'
              : `${progress.percent}% — ${progress.current}/${progress.total}`}
        </Text>
        <ChevronUp className='h-4 w-4 text-alba-gray-500' strokeWidth={1.8} />
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-4 right-4 z-40 flex w-[360px] flex-col gap-3 rounded-2xl ${surface} ${accent} p-4`}
      role='status'
      aria-live='polite'
    >
      <div className='flex items-start gap-3'>
        <div className='mt-0.5 shrink-0'>
          <Icon />
        </div>
        <div className='flex-1'>
          <Text className='text-sm font-bold text-alba-black-500'>
            {headline}
          </Text>
          <Text className='mt-0.5 text-xs text-alba-gray-600'>{subhead}</Text>
        </div>
        <div className='flex shrink-0 items-center gap-1'>
          <button
            type='button'
            onClick={() => setCollapsed(true)}
            className='rounded p-1 hover:bg-black/5'
            aria-label='Minimizar widget de importação'
          >
            <ChevronDown
              className='h-4 w-4 text-alba-gray-500'
              strokeWidth={1.8}
            />
          </button>
          {onDismiss && (isFailed || isCompleted) && (
            <button
              type='button'
              onClick={onDismiss}
              className='rounded p-1 hover:bg-black/5'
              aria-label='Fechar widget de importação'
            >
              <X className='h-4 w-4 text-alba-gray-500' strokeWidth={1.8} />
            </button>
          )}
        </div>
      </div>

      {!isFailed && !isCompleted && (
        <div className='flex flex-col gap-1.5'>
          <div className='flex items-center justify-between'>
            <Text className='text-xs text-alba-gray-600'>
              {progress.current} de {progress.total}
            </Text>
            <Text className='text-xs font-semibold text-alba-black-500'>
              {progress.percent}%
            </Text>
          </div>
          <div
            className='h-1.5 w-full overflow-hidden rounded-full bg-alba-gray-200'
            role='progressbar'
            aria-valuenow={progress.percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className='h-full rounded-full bg-alba-blue-500 transition-[width] duration-500 ease-out'
              style={{ width: `${progress.percent}%` }}
            />
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <div className='rounded-md border border-alba-error-red-500 bg-alba-white-400 p-2'>
          <button
            type='button'
            className='flex w-full items-center justify-between text-left'
            onClick={() => setExpandedErrors(prev => !prev)}
          >
            <Text className='text-xs font-semibold text-alba-error-red-600'>
              {errors.length} erro(s)
            </Text>
            <Text className='text-[10px] text-alba-error-red-600'>
              {expandedErrors ? 'ocultar' : 'detalhes'}
            </Text>
          </button>
          {expandedErrors && (
            <ul className='mt-2 max-h-32 list-disc overflow-y-auto pl-4 text-[11px] text-alba-error-red-600'>
              {errors.slice(0, 50).map((e, i) => (
                <li key={i}>
                  {e.rowNumber != null ? `linha ${e.rowNumber}` : ''}
                  {e.field ? ` (${e.field})` : ''}
                  {e.rowNumber != null || e.field ? ': ' : ''}
                  {e.message}
                </li>
              ))}
              {errors.length > 50 && (
                <li>… e mais {errors.length - 50} erros.</li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
